import {
  HTMLAttributeAnchorTarget,
  HTMLAttributes,
  type MouseEvent,
  type ReactNode,
  forwardRef,
  useCallback,
  useMemo,
} from 'react';
import { type To } from 'react-router-dom';

import { useScreenSize, useSidebar } from '../../hooks';
import { BaseAccordion, BaseAccordionItem } from '../BaseAccordion';
import { BaseTooltip } from '../BaseTooltip';
import {
  ButtonItem,
  ExpandButton,
  ExpandButtonContent,
  LeftPartButtonItem,
  LeftPartButtonItemSlot,
  LinkItem,
  NavLinkCustom,
  RightPartExpandButton,
  SidebarMultipleContent,
  Text,
} from './styles';

export interface ISidebarItemDefaultProps {
  children?: ReactNode;
  dataCy?: string;
  icon?: ReactNode;
  isActive?: boolean;
  isShrunken?: boolean;
  target?: HTMLAttributeAnchorTarget;
  title?: string;
}

export type ISidebarButtonProps = Omit<
  ISidebarItemDefaultProps &
    Pick<HTMLAttributes<HTMLElement>, 'className' | 'onClick' | 'style'>,
  'children'
>;

interface ISidebarButtonExpandItemProps {
  accordionValue: string;
  customButtonTrigger?: (props: ISidebarButtonProps) => JSX.Element;
  disableAccordion?: boolean;
  end?: undefined;
  noArrow?: boolean;
  onClick?: undefined;
  onExpandClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  target?: undefined;
  to?: undefined;
  type: 'button-accordion';
}

interface ISidebarExpandItemProps {
  accordionValue: string;
  customButtonTrigger?: undefined;
  disableAccordion?: boolean;
  end?: undefined;
  noArrow?: boolean;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  onExpandClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  target?: undefined;
  to?: undefined;
  type: 'accordion';
}

interface ISidebarExpandLinkItemProps {
  accordionValue: string;
  customButtonTrigger?: undefined;
  disableAccordion?: boolean;
  end?: boolean;
  noArrow?: boolean;
  onClick?: undefined;
  onExpandClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  target?: HTMLAttributeAnchorTarget;
  to: To;
  type: 'accordionLink';
}

interface ISidebarButtonItemProps {
  accordionValue?: undefined;
  customButtonTrigger?: undefined;
  disableAccordion?: undefined;
  end?: undefined;
  noArrow?: undefined;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  onExpandClick?: undefined;
  target?: undefined;
  to?: undefined;
  type: 'button';
}

interface ISidebarLinkItemProps {
  accordionValue?: undefined;
  customButtonTrigger?: undefined;
  disableAccordion?: undefined;
  end?: boolean;
  noArrow?: undefined;
  onClick?: undefined;
  onExpandClick?: undefined;
  target?: HTMLAttributeAnchorTarget;
  to: To;
  type: 'link';
}

export type ISidebarItemProps = ISidebarItemDefaultProps &
  (
    | ISidebarButtonExpandItemProps
    | ISidebarButtonItemProps
    | ISidebarExpandItemProps
    | ISidebarExpandLinkItemProps
    | ISidebarLinkItemProps
  );

type ISidebarItemRef = HTMLAnchorElement & HTMLButtonElement & HTMLDivElement;

export const SidebarItem = forwardRef<ISidebarItemRef, ISidebarItemProps>(
  // eslint-disable-next-line no-restricted-syntax
  function SidebarItem(
    {
      accordionValue,
      customButtonTrigger,
      children,
      dataCy = 'sidebar-item',
      disableAccordion,
      end,
      icon,
      isShrunken = false,
      noArrow = false,
      onClick,
      onExpandClick,
      target,
      title,
      to,
      type,
      isActive,
    },
    ref
  ): JSX.Element {
    const { subAccordionSidebarValue, handleSubAccordionSidebar } =
      useSidebar();
    const { isMobile } = useScreenSize();

    const handleSubAccordionSidebarChange = useCallback(
      (value: string) => {
        handleSubAccordionSidebar(value);
      },
      [handleSubAccordionSidebar]
    );
    const isShrunkenResponsive = useMemo(
      () => (isMobile ? !isShrunken : isShrunken),
      [isShrunken, isMobile]
    );

    return type === 'button-accordion' ? (
      <BaseAccordionItem
        data-cy={dataCy}
        disabled={disableAccordion}
        ref={ref}
        value={accordionValue}
      >
        <BaseTooltip
          disabled={!isShrunkenResponsive}
          disableHoverableContent
          message={title}
        >
          <SidebarMultipleContent $isShrunken={isShrunkenResponsive}>
            {customButtonTrigger ? (
              <LeftPartButtonItemSlot
                $isShrunken={isShrunken}
                onClick={onClick}
              >
                {customButtonTrigger({
                  dataCy,
                  icon,
                  isShrunken: isShrunkenResponsive,
                  title,
                })}
              </LeftPartButtonItemSlot>
            ) : (
              <LeftPartButtonItem
                $isShrunken={isShrunken}
                data-cy={dataCy}
                onClick={onClick}
              >
                {icon}
                <Text $isShrunken={isShrunken}>{title}</Text>
              </LeftPartButtonItem>
            )}
            {noArrow !== true && (
              <RightPartExpandButton
                $isShrunken={isShrunkenResponsive}
                noArrow={isShrunkenResponsive}
                onClick={onExpandClick}
              />
            )}
            {noArrow === true && isMobile && (
              <RightPartExpandButton
                $isShrunken={isShrunkenResponsive}
                noArrow={isShrunkenResponsive}
                onClick={onExpandClick}
              />
            )}
          </SidebarMultipleContent>
        </BaseTooltip>
        <ExpandButtonContent $isShrunken={isShrunkenResponsive}>
          <BaseAccordion
            type="single"
            collapsible
            onValueChange={handleSubAccordionSidebarChange}
            value={subAccordionSidebarValue ?? ''}
          >
            {children}
          </BaseAccordion>
        </ExpandButtonContent>
      </BaseAccordionItem>
    ) : type === 'accordion' ? (
      <BaseAccordionItem
        data-cy={dataCy}
        disabled={disableAccordion}
        ref={ref}
        value={accordionValue}
      >
        <BaseTooltip
          disabled={!isShrunkenResponsive}
          disableHoverableContent
          message={title}
        >
          <ExpandButton
            noArrow={noArrow || isShrunkenResponsive}
            $isShrunken={isShrunken}
            onClick={onExpandClick}
          >
            {icon}
            <Text $isShrunken={isShrunken}>{title}</Text>
          </ExpandButton>
        </BaseTooltip>
        <ExpandButtonContent $isShrunken={isShrunkenResponsive}>
          <BaseAccordion
            type="single"
            collapsible
            onValueChange={handleSubAccordionSidebarChange}
            value={subAccordionSidebarValue ?? ''}
          >
            {children}
          </BaseAccordion>
        </ExpandButtonContent>
      </BaseAccordionItem>
    ) : type === 'accordionLink' ? (
      <BaseAccordionItem
        data-cy={dataCy}
        disabled={disableAccordion}
        ref={ref}
        value={accordionValue}
      >
        <BaseTooltip
          disabled={!isShrunkenResponsive}
          disableHoverableContent
          message={title}
        >
          <NavLinkCustom to={to} end={end} target={target}>
            <ExpandButton
              $isShrunken={isShrunkenResponsive}
              noArrow={noArrow || isShrunken}
              onClick={onExpandClick}
            >
              {icon}
              <Text $isShrunken={isShrunken}>{title}</Text>
            </ExpandButton>
          </NavLinkCustom>
        </BaseTooltip>
        <ExpandButtonContent $isShrunken={isShrunkenResponsive}>
          {children}
        </ExpandButtonContent>
      </BaseAccordionItem>
    ) : type === 'button' ? (
      <BaseTooltip
        disabled={!isShrunkenResponsive}
        disableHoverableContent
        message={title}
      >
        <ButtonItem
          $isShrunken={isShrunken}
          data-cy={dataCy}
          onClick={onClick}
          ref={ref}
        >
          {icon}
          <Text $isShrunken={isShrunken}>{title ?? children}</Text>
        </ButtonItem>
      </BaseTooltip>
    ) : (
      <BaseTooltip
        disabled={!isShrunkenResponsive}
        disableHoverableContent
        message={title}
      >
        <LinkItem
          $isShrunken={isShrunkenResponsive}
          className={isActive === true ? 'active' : ''}
          data-cy={dataCy}
          end={end}
          ref={ref}
          target={target}
          to={to}
        >
          {icon}
          <Text $isShrunken={isShrunken}>{title ?? children}</Text>
        </LinkItem>
      </BaseTooltip>
    );
  }
);
